/**
 *
 * Stripped down variant selector for use in the sticky bar
 *
 */
if (!customElements.get('sticky-variant-selects') && customElements.get('variant-selects')) {
    customElements.define(
        'sticky-variant-selects',
        class StickyVariantSelects extends VariantSelects {
            constructor() {
                super();
            }

            connectedCallback() {
                this.parent = this.closest('sticky-product-info');
                this.sectionId = this.parent.getAttribute('data-section');
                this.originalId = this.parent.getAttribute('data-original-section');
                this.currentVariant = null;
                super.updateOptions();
                super.updateMasterId();
                this.variantChangeUnsubscriber = subscribe(
                    PUB_SUB_EVENTS.variantChange,
                    (event) => {
                        if (!event || !event.data || !event.data.variant) return;
                        if (this.currentVariant && this.currentVariant.id === event.data.variant.id)
                            return;
                        this.currentVariant = event.data.variant;
                        this.onChangeBroadcast();
                    }
                );
            }

            disconnectedCallback() {
                if (this.variantChangeUnsubscriber) {
                    this.variantChangeUnsubscriber();
                }
            }

            onChangeBroadcast() {
                if (!this.currentVariant) return;
                const variantSelectors = this.querySelectorAll('select');
                variantSelectors.forEach((variantSelector, index) => {
                    variantSelector.value = this.currentVariant.options[index];
                });
                super.updateOptions();
            }

            onVariantChange(event) {
                super.updateOptions();
                super.updateMasterId();
                const partnerSelect = document.getElementById(`variant-selects-${this.originalId}`);
                if (!partnerSelect) return;
                const fieldsets = partnerSelect.querySelectorAll('fieldset');
                const variantSelectors = partnerSelect.querySelectorAll('select');
                variantSelectors.forEach((variantSelector, index) => {
                    if (variantSelector.value !== this.options[index]) {
                        variantSelector.value = this.options[index];
                        variantSelector.dispatchEvent(new Event('change', {bubbles: true}));
                    }
                });
                fieldsets.forEach((fieldset, index) => {
                    fieldset.querySelectorAll('input').forEach((radio) => {
                        if (radio.value === this.options[index] && !radio.checked) {
                            radio.checked = true;
                            radio.dispatchEvent(new Event('change', {bubbles: true}));
                        }
                    });
                });
            }
        }
    );
}

/***
 *
 * Sticky Product Form
 *
 * This is a simple wrapper that listens to broadcast variant change events and updates child content.
 * It will also sync updates from said child content back to the main product form
 *
 */

if (!customElements.get('sticky-product-info')) {
    customElements.define(
        'sticky-product-info',
        class StickyProductInfo extends HTMLElement {
            constructor() {
                super();
            }

            connectedCallback() {
                this.yieldTasks([
                    this.setElements.bind(this),
                    this.setQuantityBoundaries.bind(this),
                    this.setHandlers.bind(this),
                    this.setSubscriptions.bind(this)
                ]);
            }

            disconnectedCallback() {
                if (this.variantChangeUnsubscriber) {
                    this.variantChangeUnsubscriber();
                }
                if (this.cartUpdateUnsubscriber) {
                    this.cartUpdateUnsubscriber();
                }
                this.partnerInput?.removeEventListener('change', this.pairQuantityChangeHandler);
                this.quantityInput?.removeEventListener('change', this.quantityChangeHandler);
            }

            yieldToMain() {
                return new Promise((resolve) => {
                    setTimeout(resolve, 0);
                });
            }

            async yieldTasks(tasks) {
                if (!tasks?.length) return;
                while (tasks.length > 0) {
                    const task = tasks.shift();
                    task();
                    await this.yieldToMain();
                }
            }

            setElements() {
                this.parent = this.closest('sticky-bar');
                this.sectionId = this.getAttribute('data-section');
                this.originalId = this.getAttribute('data-original-section');
                this.quantityInput = this.querySelector('quantity-input');
                this.input = this.querySelector('.quantity__input');
                this.partnerInput = document.getElementById(`Quantity-${this.originalId}`);
                this.variantIDInput = this.querySelector('input[name="id"]');
                this.quantityForm = this.querySelector('.product-form__quantity');
                this.currentVariant = this.querySelector('sticky-variant-selects').currentVariant;
            }

            setHandlers() {
                this.quantityChangeHandler = this.onQuantityChange.bind(this);
                this.pairQuantityChangeHandler = this.onPairQuantityChange.bind(this);
                this.quantityInput?.addEventListener('change', this.quantityChangeHandler);
                this.partnerInput?.addEventListener('change', this.pairQuantityChangeHandler);
            }

            setSubscriptions() {
                this.cartUpdateUnsubscriber = subscribe(
                    PUB_SUB_EVENTS.cartUpdate,
                    this.fetchQuantityRules.bind(this)
                );

                this.variantChangeUnsubscriber = subscribe(
                    PUB_SUB_EVENTS.variantChange,
                    (event) => {
                        this.onVariantChangeBroadcast(event);
                    }
                );
            }

            onVariantChangeBroadcast(event) {
                if (!event || !event.data || !event.data.variant) return;
                if (this.currentVariant && this.currentVariant.id === event.data.variant.id) return;
                this.currentVariant = event.data.variant;

                this.yieldTasks([
                    this.updateVariantInput.bind(this),
                    this.toggleAddButton.bind(this, event.data.html),
                    this.updatePrice.bind(this, event.data.html),
                    this.updateQuantityRules.bind(this, event.data.sectionId, event.data.html),
                    this.setQuantityBoundaries.bind(this)
                ]);
            }

            onPairQuantityChange(event) {
                const value = event?.target?.value || 1;
                this.input.value = value;
                this.quantityInput.validateQtyRules();
            }

            onQuantityChange(event) {
                if (!event || !event.target || !event.target.value || !this.partnerInput) return;
                const quantity = event.target.value;
                this.partnerInput.value = quantity;
                this.partnerInput.dispatchEvent(new Event('change', {bubbles: true}));
            }

            fetchQuantityRules() {
                if (!this.currentVariant) return;
                fetch(
                    `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.originalId}`
                )
                    .then((response) => {
                        return response.text();
                    })
                    .then((responseText) => {
                        const html = new DOMParser().parseFromString(responseText, 'text/html');
                        this.updateQuantityRules(this.originalId, html);
                        this.setQuantityBoundaries();
                    })
                    .catch((e) => {
                        console.error(e);
                    });
            }

            setQuantityBoundaries() {
                const data = {
                    cartQuantity: this.input.getAttribute('data-cart-quantity')
                        ? parseInt(this.input.getAttribute('data-cart-quantity'), 10)
                        : 0,
                    min: this.input.getAttribute('data-min')
                        ? parseInt(this.input.getAttribute('data-min'), 10)
                        : 1,
                    max: this.input.getAttribute('data-max')
                        ? parseInt(this.input.getAttribute('data-max'), 10)
                        : null,
                    step: this.input.getAttribute('data-step')
                        ? parseInt(this.input.getAttribute('data-step'), 10)
                        : 1
                };

                let min = data.min;
                const max = data.max === null ? data.max : data.max - data.cartQuantity;
                if (max !== null) min = Math.min(min, max);
                if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

                this.input.min = min;
                this.input.max = max;
                this.input.value = min;
            }

            updateQuantityRules(sectionId, html) {
                if (!sectionId || !html) return;
                const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
                const updated = quantityFormUpdated?.querySelector('.quantity__input');
                if (!updated) return;

                const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
                for (let attribute of attributes) {
                    const valueUpdated = updated.getAttribute(attribute);
                    if (valueUpdated !== null) this.input.setAttribute(attribute, valueUpdated);
                }
            }

            updatePrice(html) {
                if (!html || !this.currentVariant) return;
                const destination = document.getElementById(`price-${this.sectionId}`);
                const source = html.getElementById(`price-${this.originalId}`);
                if (source && destination) destination.innerHTML = source.innerHTML;
            }

            updateVariantInput(html) {
                if (!this.currentVariant) return;
                this.variantIDInput.value = this.currentVariant.id;
            }

            toggleAddButton(html) {
                if (!this.currentVariant || !html) return;
                const button = this.querySelector(`product-form button[type="submit"]`);
                const buttonText = button?.querySelector('span');
                const addButtonUpdated = html.getElementById(
                    `ProductSubmitButton-${this.originalId}`
                );

                if (
                    this.currentVariant.available ||
                    !addButtonUpdated ||
                    !addButtonUpdated.hasAttribute('disabled')
                ) {
                    button?.classList.remove('disabled');
                    button?.removeAttribute('disabled');
                    if (buttonText) buttonText.textContent = window.variantStrings.addToCart;
                    return;
                }

                button?.classList.add('disabled');
                button?.setAttribute('disabled', true);

                if (buttonText) buttonText.textContent = window.variantStrings.soldOut;
            }
        }
    );
}
